import { Identify, Types, createInstance } from "@amplitude/analytics-browser";
import { Context } from "@nuxt/types";
import Vue from "vue";

import { isAuth0UserToken } from "@/schemes/auth0";
import { isSigilUserToken } from "@/schemes/sigil";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCreatorShop } from "@/store/creator-shop";
import { $config } from "@/utils/accessors/config";
import { OK } from "@/utils/enums/http-codes";

import { DeviceIdentificationPlugin } from "./plugins";

export interface IdentifyUserParams {
  userId: string;
  email: string;
  rsAccountId?: number | null;
  rsPublisherId?: number | null;
}

export interface SetIdentityParams {
  name: string;
  value: Types.ValidPropertyType;
}

export function isAmplitudeResult(result: unknown): result is Types.Result {
  return result !== null && typeof result === "object" && "message" in result;
}

export default class Amplitude {
  public client: Types.BrowserClient = createInstance();
  public context!: Context;
  public vm: Vue;
  public throttledEvents: Set<AmplitudeEventType> = new Set();

  public constructor(apiKey: string, context: Context, config?: Types.BrowserOptions) {
    this.context = context;

    const userId =
      context.$auth.user === null
        ? undefined
        : isAuth0UserToken(context.$auth.user)
        ? context.$auth.user["http://shopltk.com/profile"].sigilUserId
        : context.$auth.user?.userId;

    let version = `${$config.version}`;
    if ($config.env === "prod") {
      // Remove any prerelease information
      version = version.split("-")[0];
    } else {
      version += `+${$config.gitSha || "development"}`;
    }

    this.client.add(
      new DeviceIdentificationPlugin({
        deviceId: null,
        platform: null,
        version,
      }),
    );
    this.client.init(apiKey, userId, config);

    const loggedInWatcher = (newValue: boolean) => {
      if (newValue === true) {
        const user = context.$auth.user;

        if (user === null) {
          return;
        }

        const email = user.email;
        const userId = isAuth0UserToken(user) ? user["http://shopltk.com/profile"].sigilUserId : user.userId;
        const rsAccountId = isSigilUserToken(user) ? user.jwt?.account_id : undefined;
        const rsPublisherId = isSigilUserToken(user) ? user.jwt?.publisher_id : undefined;

        this.identifyUser({ userId, email, rsAccountId, rsPublisherId });
      }
    };

    // Until this app switches to Vue3, this is the way for plugins/external modules to make reactive watchers
    const vm = new Vue({
      // Using the auth $state object so the reactivity is maintained into the vue instance
      data: { $auth: context.$auth },
      watch: { "$data.$auth.loggedIn": loggedInWatcher },
    });
    this.vm = vm;

    /**
     * Recommended method to flush any queued events.
     * This is a catchall in case some other usage unexpectedly adds events to a queue. Creates a
     * new listener for every new client created.
     *
     * @see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/migration/#flush-or-onexitpage
     */
    window.addEventListener("pagehide", () => {
      this.client.setTransport(Types.TransportType.SendBeacon);
      this.client.flush();
    });
  }

  public getBaseEventData() {
    const { route, from, store } = this.context;
    const amplitudeStore = getAmplitudeStore(store);
    const page = route?.name ?? "";
    const rootReferrer = amplitudeStore.rootReferrer;
    const referringComponent = amplitudeStore.referringComponent;
    const referringCreatorShopTab = useCreatorShop().getReferringShopTab;
    const referrer = from?.name ?? rootReferrer;

    return {
      ...{
        page,
        referrer,
        source: page,
        "root-referrer": rootReferrer,
      },
      // only include property if a value is present
      ...(referringComponent && { "referring-component": referringComponent }),
      ...(referringCreatorShopTab && { "creator-shop-tab": referringCreatorShopTab }),
    };
  }

  public async sendEvent<T extends AmplitudeEventType>(
    type: T,
    data?: ExtractEventData<AmplitudeEvent, T>,
  ): Promise<void | string> {
    const { store } = this.context;
    const baseEventData: BaseEventProps = this.getBaseEventData();
    const eventData = Object.assign(baseEventData, data);

    getAmplitudeStore(store).setReferringComponent(null);
    useCreatorShop().setReferringShopTab(null);

    try {
      const result = await this.client.track(type, eventData).promise;

      if (result.code === OK) {
        return;
      }

      return result.message;
    } catch (error) {
      if (isAmplitudeResult(error)) {
        return error.message;
      }

      return `${error}`;
    }
  }

  public async throttledSendEvent<T extends AmplitudeEventType>(
    type: T,
    data?: ExtractEventData<AmplitudeEvent, T>,
    time?: number,
  ): Promise<void | string> {
    if (!this.context?.$amplitude?.throttledEvents.has(type)) {
      const DEFAULT_TIME = 500;
      this.context?.$amplitude?.throttledEvents.add(type);
      await this.sendEvent(type, data);
      setTimeout(() => {
        this.context?.$amplitude?.throttledEvents.delete(type);
      }, time || DEFAULT_TIME);
    }
  }

  /**
   * Temporarily set the transport to a `beacon` type so that network requests won't be canceled
   * when leaving the page. Mostly applicable for mobile/IAB browsers, as desktop reliably opens
   * a new tab
   */
  public async sendExitEvent<T extends AmplitudeEventType>(
    type: T,
    data?: ExtractEventData<AmplitudeEvent, T>,
  ): Promise<void | string> {
    this.client.setTransport(Types.TransportType.SendBeacon);

    try {
      await this.sendEvent(type, data);
    } finally {
      this.client.flush();
      this.client.setTransport(Types.TransportType.Fetch);
    }
  }

  public async sendScrollEvent(eventName: AmplitudeEventType, length?: number) {
    const ltksPerPage = 30;
    const page = length ? Math.ceil(length / ltksPerPage) : 1;
    await this.sendEvent(eventName, { page });
  }

  public async sendDownloadAppEvent(
    referringComponent = "",
    deviceFamily = "",
    bannerName?: string,
    journeyName?: string,
  ) {
    if (process.client) {
      await this.sendExitEvent("/click-app-store", {
        "referring-component": referringComponent,
        "app-store-type": deviceFamily || this.getMobileOperatingSystem(),
        "branch-journey": journeyName,
        "branch-banner-name": bannerName,
      });
    }
  }

  public getMobileOperatingSystem() {
    const { userAgent } = window.navigator;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return "Windows Phone";
    }

    if (/android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return "iOS";
    }

    return "unknown";
  }

  public logout() {
    this.client.reset();
  }

  public getDeviceId() {
    return this.client.getDeviceId();
  }

  public async identifyUser({
    userId,
    email,
    rsAccountId = null,
    rsPublisherId = null,
  }: IdentifyUserParams): Promise<void | string> {
    const identity = new Identify();

    this.client.setUserId(userId);
    identity.set("email", email);
    if (rsAccountId !== null) {
      identity.set("rs-account-id", rsAccountId);
    }
    if (rsPublisherId !== null) {
      identity.set("rs-publisher-id", rsPublisherId);
    }

    try {
      const result = await this.client.identify(identity).promise;

      if (result.code === OK) {
        return;
      }

      return result.message;
    } catch (error) {
      if (isAmplitudeResult(error)) {
        return error.message;
      }

      return `${error}`;
    }
  }

  public async setIdentity(props: SetIdentityParams[]): Promise<void | string | boolean> {
    const identity = new Identify();

    props.forEach(({ name, value }: SetIdentityParams) => {
      if (value || value === false) {
        identity.set(name, value);
      }
    });

    try {
      const result = await this.client.identify(identity).promise;

      if (result.code === OK) {
        return true;
      }

      return result.message;
    } catch (error) {
      if (isAmplitudeResult(error)) {
        return error.message;
      }

      return `${error}`;
    }
  }

  getReferringComponentByPage(page?: string | null, section?: string) {
    switch (page) {
      case "account-favorites-products":
        return `favorites page ${section}`;
      case "categories-category":
        return section === "hashtag" ? "post page hashtag" : "category details page";
      case "collections-hash":
        return "wishlist details page";
      case "explore-displayName-posts-id":
        return section ? `post page ${section}` : "post page main section";
      case "explore-displayName-collections":
        return "creator shop collection listing page";
      case "explore-displayName":
        return section ? `creator shop ${section}` : "creator shop post feed";
      case "explore-displayName-collections-id":
        return "creator shop collection detail page";
      case "explore-displayName-productsets":
        return "creator shop product set listing page";
      case "explore-displayName-giftguides":
        return "creator shop gift guides listing page";
      case "explore-displayName-productsets-id":
        return section ? `product set detail page ${section}` : "product set detail page profile image";
      case "explore-displayName-giftguides-id":
        return section ? `gift guide detail page ${section}` : "gift guide detail page";
      case "giftguides-audience":
        return "gift guide category listing page";
      case "home-following":
        return `home - following page feed`;
      case "home-for-you":
        return `home - for you page feed`;
      case "search":
        return section ? `search results ${section} page` : "search results page";
      default:
        return "";
    }
  }
}
